import { buildArticleTagMemoParams } from '@/api/document';
import { TrackingBaseData } from '@/api/tracking';
import axios from 'axios';
import { Dayjs } from 'dayjs';
import Qs from 'qs';
import {
  AdpDocument,
  Article,
  ConfirmForgotPasswordResponse,
  CurrentUserResponse,
  FeedType,
  ForgotPasswordResponse,
  Group,
  Lang,
  MyTag,
  NotificationItem,
  NotificationType,
  PersonalSurveyProfileTrackingCategory,
  SetNewPasswordResponse,
  SignInResponse,
  Tag,
  ThemeList,
  UserEntity,
  UserInfo,
  UserItemSearchPreference,
  UserRole,
  UserSearchPreference,
  UserSettings,
  UserSettingsEmail,
  UserSettingsNews,
  UserSettingsNotificationRequest,
  UserSettingsProfile,
  WebPushSubscription,
} from '@/types';
import { userSession } from '@/utils/userSession';

export async function fetchUserInfo(userId: number | null): Promise<UserInfo> {
  const { data } = await axios.get<UserInfo>(
    `/users/${userId}?include_action_counts=false`,
  );
  return data;
}

export async function fetchMyInfo(): Promise<UserInfo> {
  const myUserId = userSession.getUserId();
  return fetchUserInfo(myUserId);
}

export async function fetchNotifications(
  limit = 10,
  page = 1,
  order: 'desc' | 'asc' = 'desc',
  notificationTypes?: NotificationType[],
  unreadOnly = false,
  trackingData = {},
): Promise<NotificationItem[]> {
  const myUserId = userSession.getUserId();
  const { data } = await axios.get<{
    notifications: NotificationItem[];
  }>(`/users/${myUserId}/notifications`, {
    params: {
      limit,
      page,
      order,
      notification_types: notificationTypes,
      unread_only: unreadOnly,
      tracking_data: trackingData,
    },
  });
  return data.notifications;
}

export async function fetchThemeList(
  feedType = 'domestic',
): Promise<ThemeList[]> {
  const myUserId = userSession.getUserId();
  const { data } = await axios.get<{ themes: ThemeList[] }>(
    `/users/${myUserId}/themes?feed_type=${feedType}&include_keyword_feed_counts=false`,
  );
  return data.themes;
}

// NOTE: SWRVを使えない箇所で使用想定
export async function fetchWebPushSubscription(
  endpoint: string,
): Promise<WebPushSubscription | undefined> {
  const userId = userSession.getUserId();
  const { data } = await axios.get<{
    web_push_subscriptions: WebPushSubscription[];
  }>(`/users/${userId}/web_push_subscriptions?${Qs.stringify({ endpoint })}`);
  return data.web_push_subscriptions[0];
}

export async function seeWebPushNotification(
  subscription: WebPushSubscription,
): Promise<void> {
  const userId = userSession.getUserId();
  await axios.patch<void>(`/users/${userId}/web_push_subscriptions/last_seen`, {
    endpoint: subscription.endpoint,
  });
}

export async function getPersonalNews(
  feedType: FeedType,
  date: Dayjs,
  page = 1,
  limit = 10,
  execution = 'morning',
): Promise<{ articles: Article[]; count: number }> {
  const myUserId = userSession.getUserId();
  const params = {
    feed_type: feedType,
    date: date.format('YYYY-MM-DD'),
    execution: execution,
    page: page,
    limit: limit,
  };
  const { data } = await axios.get<{
    personal_feeds: Article[];
    count: number;
  }>(`/users/${myUserId}/personal_feeds?${Qs.stringify(params)}`);
  return { articles: data.personal_feeds, count: data.count };
}

export async function signout(): Promise<void> {
  await axios.post<void>('/users/signout');
}

export async function signin(
  email: string,
  password: string,
): Promise<SignInResponse> {
  const { data } = await axios.post<SignInResponse>('/users/signin', {
    email: email,
    password: password,
  });
  return data;
}

export async function samlSignin(
  email: string,
  redirect?: string,
): Promise<void> {
  const { data } = await axios.post<{ login_url: string }>('/saml', {
    email: email,
    redirect_url: redirect,
  });
  document.location.href = data.login_url;
}

export async function currentUser(): Promise<CurrentUserResponse> {
  const { data } = await axios.get<CurrentUserResponse>('/users/current_user');
  return data;
}

export async function deleteUser(userId: number): Promise<UserEntity> {
  const { data } = await axios.delete<UserEntity>(`/users/${userId}`);
  return data;
}

export async function updateUser(
  userId: number,
  email: string,
  userName: string,
  role: UserRole,
  productContractId: string | null,
  organizationTagIds: number[],
): Promise<UserEntity> {
  const { data } = await axios.put<UserEntity>(`/users/${userId}`, {
    user_name: userName,
    role: role,
    email: email,
    product_contract_id: productContractId,
    organization_tag_ids: organizationTagIds,
  });
  return data;
}

export async function patchUser(param: {
  agents_recommendation_dismissed?: true;
}): Promise<void> {
  const userId = userSession.getUserId();
  await axios.patch<void>(`/users/${userId}`, { ...param });
}

export async function createUser(
  email: string,
  userName: string,
  role: UserRole,
  productContractId: string | null,
  organizationTagIds: number[],
): Promise<UserEntity> {
  const { data } = await axios.post<UserEntity>('/users', {
    email: email,
    user_name: userName,
    role: role,
    product_contract_id: productContractId,
    organization_tag_ids: organizationTagIds,
  });
  return data;
}

export async function set_new_password(
  email: string,
  password: string,
  session: string,
): Promise<SetNewPasswordResponse> {
  const { data } = await axios.post<SetNewPasswordResponse>(
    '/users/set_new_password',
    {
      email: email,
      password: password,
      session: session,
    },
  );
  return data;
}

export async function changePassword(
  oldPassword: string,
  newPassword: string,
  trackingBaseData: TrackingBaseData,
): Promise<void> {
  const userId = userSession.getUserId();
  await axios.post<void>(`/users/${userId}/change_password`, {
    old_password: oldPassword,
    new_password: newPassword,
    tracking_data: {
      page: {
        name: trackingBaseData.pageName,
        url: trackingBaseData.pageUrl,
      },
    },
  });
}

export async function forgotPassword(
  email: string,
): Promise<ForgotPasswordResponse> {
  const { data } = await axios.post<ForgotPasswordResponse>(
    '/users/forgot_password',
    {
      email: email,
    },
  );
  return data;
}

export async function confirmForgotPassword(
  email: string,
  password: string,
  confirmation_code: string,
): Promise<ConfirmForgotPasswordResponse> {
  const { data } = await axios.post<ConfirmForgotPasswordResponse>(
    '/users/confirm_forgot_password',
    {
      email: email,
      password: password,
      confirmation_code: confirmation_code,
    },
  );
  return data;
}

export async function postFavoriteTheme(
  themeId: number,
): Promise<{ order: number }> {
  const myUserId = userSession.getUserId();
  const { data } = await axios.post<{ order: number }>(
    `/users/${myUserId}/themes/${themeId}/favorites`,
  );
  return data;
}

export async function updateFavoriteTheme(
  themeId: number,
  order: number,
): Promise<void> {
  const myUserId = userSession.getUserId();
  await axios.put<void>(`/users/${myUserId}/themes/${themeId}/favorites`, {
    order: order,
  });
}

export async function deleteFavoriteTheme(themeId: number): Promise<void> {
  const myUserId = userSession.getUserId();
  await axios.delete<void>(`/users/${myUserId}/themes/${themeId}/favorites`);
}

export async function getUserSettings(
  userId: number | null = null,
): Promise<UserSettings> {
  if (userId === null) {
    userId = userSession.getUserId();
  }
  const { data } = await axios.get<UserSettings>(`/users/${userId}/settings`);
  return data;
}

// FIXME: users setting getの処理がfatすぎるので取得するpropertyを絞る
export async function updateUserSettings(
  settings: UserSettings,
  trackingBaseData: TrackingBaseData,
  trackingCategory?: PersonalSurveyProfileTrackingCategory,
): Promise<void> {
  const myUserId = userSession.getUserId();
  await axios.put<void>(`/users/${myUserId}/settings`, {
    ...settings,
    tracking_data: {
      page: {
        name: trackingBaseData.pageName,
        url: trackingBaseData.pageUrl,
      },
      ...(trackingCategory ? { category: trackingCategory } : {}),
    },
  });
}

// NOTE: useUserSettingsをdeleteするまでの対応
export async function updateUserSettingsEmail(
  settings: UserSettingsEmail,
  trackingBaseData: TrackingBaseData,
  trackingCategory?: PersonalSurveyProfileTrackingCategory,
): Promise<void> {
  const myUserId = userSession.getUserId();
  await axios.put<void>(`/users/${myUserId}/settings`, {
    ...settings,
    tracking_data: {
      page: {
        name: trackingBaseData.pageName,
        url: trackingBaseData.pageUrl,
      },
      ...(trackingCategory ? { category: trackingCategory } : {}),
    },
  });
}

export async function updateUserSettingsNotification(
  notificationSettings: UserSettingsNotificationRequest,
  trackingBaseData: TrackingBaseData,
): Promise<void> {
  const myUserId = userSession.getUserId();
  await axios.put<void>(`/users/${myUserId}/settings/notification`, {
    ...notificationSettings,
    tracking_data: {
      page: {
        name: trackingBaseData.pageName,
        url: trackingBaseData.pageUrl,
      },
    },
  });
}

export async function updateUserSettingsNews(
  settings: UserSettingsNews,
  trackingBaseData: TrackingBaseData,
  trackingCategory?: PersonalSurveyProfileTrackingCategory,
): Promise<void> {
  const myUserId = userSession.getUserId();
  await axios.put<void>(`/users/${myUserId}/settings`, {
    ...settings,
    tracking_data: {
      page: {
        name: trackingBaseData.pageName,
        url: trackingBaseData.pageUrl,
      },
      ...(trackingCategory ? { category: trackingCategory } : {}),
    },
  });
}

export async function excludePersonalFeedsSiteAndArticle(
  articleId: number,
  lang: Lang,
): Promise<void> {
  const myUserId = userSession.getUserId();
  await axios.post<void>(
    `/users/${myUserId}/settings/personal_feeds_site_exclusions`,
    {
      article_id: articleId,
      lang,
    },
  );
}

export async function updateUserSettingsProfile(
  settings: UserSettingsProfile,
  trackingBaseData: TrackingBaseData,
  trackingCategory?: PersonalSurveyProfileTrackingCategory,
): Promise<void> {
  const myUserId = userSession.getUserId();
  await axios.put<void>(`/users/${myUserId}/settings`, {
    ...settings,
    tracking_data: {
      page: {
        name: trackingBaseData.pageName,
        url: trackingBaseData.pageUrl,
      },
      ...(trackingCategory ? { category: trackingCategory } : {}),
    },
  });
}

export async function resendPasswordEmail(userId: number): Promise<void> {
  await axios.post<void>(`/users/${userId}/admin_reset_password`);
}

export async function fetchGroups(userId: number): Promise<Group[]> {
  const { data } = await axios.get<{ groups: Group[] }>(
    `/users/${userId}/groups?include_unread_comment_count=false`,
  );
  return data.groups;
}

export async function updateLastViewedTimeForGroup(
  groupId: number,
): Promise<void> {
  const userId = userSession.getUserId();
  await axios.put<void>(`/users/${userId}/groups/${groupId}/view`);
}

export async function updateUserArticleMemo(
  adpDocument: AdpDocument,
  memo: string,
): Promise<void> {
  const userId = userSession.getUserId();
  await axios.put<void>(`/users/${userId}/articles/${adpDocument.id}/memos`, {
    content: memo,
    ...buildArticleTagMemoParams(adpDocument),
  });
}

export async function deleteUserArticleMemo(
  adpDocument: AdpDocument,
): Promise<void> {
  const userId = userSession.getUserId();
  const params = Qs.stringify(buildArticleTagMemoParams(adpDocument));
  await axios.delete<void>(
    `/users/${userId}/articles/${adpDocument.id}/memos?${params}`,
  );
}

export async function updateUserArticleTags(
  adpDocument: AdpDocument,
  tags: string[],
): Promise<void> {
  const userId = userSession.getUserId();
  await axios.put<void>(`/users/${userId}/articles/${adpDocument.id}/tags`, {
    tags,
    ...buildArticleTagMemoParams(adpDocument),
  });
}

export async function fetchUserArticleTags(userId: number): Promise<Tag[]> {
  const { data } = await axios.get<{ user_tags: Tag[] }>(
    `/users/${userId}/tags`,
  );
  return data.user_tags;
}

export async function viewNotifications(
  notificationTypes?: readonly NotificationType[] | undefined,
): Promise<void> {
  const userId = userSession.getUserId();
  await axios.put<void>(`/users/${userId}/notifications/view`, {
    notification_types: notificationTypes,
  });
}

export async function readNotifications(
  notificationTypes?: readonly NotificationType[],
): Promise<void> {
  const userId = userSession.getUserId();
  await axios.put<void>(`/users/${userId}/notifications/read`, {
    notification_types: notificationTypes,
  });
}

export async function readNotification(notificationId: number): Promise<void> {
  const userId = userSession.getUserId();
  try {
    await axios.put<void>(
      `/users/${userId}/notification/${notificationId}/read`,
    );
  } catch (error) {
    // タブ間・並行リクエストで既読化が二重に走ると、バックエンドが
    // 400 'Exists read_at' を返す。既読という結果は同じで実害がないため
    // この 400 のみ握りつぶす（それ以外は呼び出し元へ再throw）。（ANDEV-9617）
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 400 &&
      (error.response.data as { error?: string } | undefined)?.error ===
        'Exists read_at'
    ) {
      return;
    }
    throw error;
  }
}

export async function addUserTheme(themeId: number): Promise<void> {
  const userId = userSession.getUserId();
  await axios.post<void>(`/users/${userId}/themes/${themeId}`);
}

export async function deleteUserTheme(themeId: number): Promise<void> {
  const userId = userSession.getUserId();
  await axios.delete<void>(`/users/${userId}/themes/${themeId}`);
}

export async function updateUserJobType(jobtTypeId: number): Promise<void> {
  const userId = userSession.getUserId();
  await axios.put<void>(`/users/${userId}/job_types`, {
    id: jobtTypeId,
  });
}

export async function updateUserNeedOnboarding(value: boolean): Promise<void> {
  const userId = userSession.getUserId();
  await axios.put<void>(`/users/${userId}/need_onboarding`, {
    value: value,
  });
}

export async function createMyTag(name: string): Promise<MyTag> {
  const userId = userSession.getUserId();
  const { data } = await axios.post<MyTag>(`/users/${userId}/my_tag`, {
    tag_name: name,
  });
  return data;
}

export async function editMyTag(myTagId: number, name: string): Promise<MyTag> {
  const userId = userSession.getUserId();
  const { data } = await axios.put<MyTag>(
    `/users/${userId}/my_tag/${myTagId}`,
    {
      tag_name: name,
    },
  );
  return data;
}

export async function deleteMyTag(myTagId: number): Promise<void> {
  const userId = userSession.getUserId();
  await axios.delete<MyTag>(`/users/${userId}/my_tag/${myTagId}`);
}

export async function updateSelectedIndustryId(
  industryId: number,
): Promise<void> {
  const userId = userSession.getUserId();
  await axios.put<void>(`/users/${userId}/settings/industry`, {
    industry_id: industryId,
  });
}

export async function getUserSearchPreference(): Promise<UserSearchPreference | null> {
  const { data } = await axios.get<{
    user_search_preference: UserSearchPreference | null;
  }>(`/users/user_search_preference`);

  return data.user_search_preference;
}

export async function updateUserSearchPreference(
  preference: UserSearchPreference,
): Promise<void> {
  // 現状sortのみ保存に対応している
  await axios.post(`/users/user_search_preference`, {
    sort: preference.sort,
  });
}

export async function updateUserItemSearchPreference(
  itemType: 'theme' | 'group',
  preference: UserItemSearchPreference,
): Promise<void> {
  // 現状sortのみ保存に対応している
  await axios.post(`/users/user_item_search_preference/${itemType}`, {
    sort: preference.sort,
  });
}
